import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getEventPlayerOpponentsByRound, swapTeamsInGames } from '../../features/event/eventSlice';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  Button,
  Snackbar,
  Backdrop
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ClearIcon from '@mui/icons-material/Clear';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  buildTeamOpponentAutoAdjustments,
  getTeamOpponentsMaxRound
} from './teamOpponentSuggestions';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none'
  }
}));

const GapTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.background.paper,
  borderRight: 'none',
  borderLeft: 'none',
  width: '80px',
  minWidth: '80px',
  maxWidth: '80px',
  padding: '0px'
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected
  }
}));

const OpponentCell = styled(TableCell)(({ theme }) => ({
  border: 'none',
  padding: theme.spacing(1),
  maxWidth: '200px',
  verticalAlign: 'top'
}));

const CurrentRoundCell = styled(TableCell)(({ theme, selected, hasOpponents }) => ({
  border: 'none',
  padding: theme.spacing(1),
  minWidth: '250px',
  verticalAlign: 'top',
  backgroundColor: selected
    ? theme.palette.primary.main + '40'
    : theme.palette.primary.light + '15',
  cursor: hasOpponents ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: hasOpponents
      ? selected
        ? theme.palette.primary.main + '60'
        : theme.palette.primary.light + '25'
      : theme.palette.primary.light + '15'
  }
}));

const GapCell = styled(TableCell)(({ theme }) => ({
  border: 'none',
  padding: '0px',
  width: '80px',
  minWidth: '80px',
  maxWidth: '80px',
  verticalAlign: 'top',
  backgroundColor: theme.palette.background.paper
}));

const SelectableTeamRow = styled(StyledTableRow)(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.primary.light + '20' : undefined,
  '&:nth-of-type(odd)': {
    backgroundColor: selected ? theme.palette.primary.light + '30' : theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.light + '40' : theme.palette.action.selected
  }
}));

const TeamOpponents = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectionBlue = '#1976d2';
  const selectionBlueLight = '#e3f2fd';

  // Redux state - now expecting teamOpponents instead of playerOpponents
  const {
    playerOpponents: teamOpponents,
    loadingPlayerOpponents,
    swappingTeams,
    error
  } = useSelector((state) => state.event);

  // Local state for calculations
  const [maxRounds, setMaxRounds] = useState(0);

  // Selection state for swapping
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [autoAdjustments, setAutoAdjustments] = useState([]);
  const [applyingAutoAdjustments, setApplyingAutoAdjustments] = useState(false);
  const [focusedTeamId, setFocusedTeamId] = useState('');
  const [dragSourceTeamId, setDragSourceTeamId] = useState('');
  const [dragOverTeamId, setDragOverTeamId] = useState('');

  useEffect(() => {
    if (eventId) {
      dispatch(getEventPlayerOpponentsByRound({ eventId }));
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    if (teamOpponents && Object.keys(teamOpponents).length > 0) {
      // Calculate max rounds for table headers
      const rounds = getTeamOpponentsMaxRound(teamOpponents);
      setMaxRounds(rounds);
    }
  }, [teamOpponents]);

  // Swap selection (original behavior)
  const handleSwapSelection = (e, teamId, teamName, currentRoundOpponents) => {
    // Disable selection while swapping
    if (swappingTeams) return;

    e.stopPropagation();

    setSelectedTeams((prev) => {
      if (prev.find((team) => team.id === teamId)) {
        // Deselect if already selected
        return prev.filter((team) => team.id !== teamId);
      } else if (prev.length < 2) {
        // Select if less than 2 teams selected
        return [
          ...prev,
          {
            id: teamId,
            name: teamName,
            currentRoundOpponents: currentRoundOpponents || []
          }
        ];
      } else {
        // Replace first selection if 2 teams already selected
        return [
          prev[1],
          {
            id: teamId,
            name: teamName,
            currentRoundOpponents: currentRoundOpponents || []
          }
        ];
      }
    });
  };

  // Focus selection for global highlighting (separate from swap)
  const handleFocusSelection = (teamId) => {
    setFocusedTeamId((prev) => (prev === teamId ? '' : teamId));
  };

  // Function to clear selections
  const handleClearSelection = () => {
    setSelectedTeams([]);
  };

  const buildSwapTeam = (teamId) => {
    if (!teamId || !teamOpponents?.[teamId]) {
      return null;
    }

    return {
      id: teamId,
      name: teamOpponents[teamId].name,
      currentRoundOpponents: teamOpponents[teamId]?.opponentsByRound?.[maxRounds] || []
    };
  };

  const executeSwap = async (teamOne, teamTwo) => {
    if (!teamOne || !teamTwo) {
      setSnackbarMessage('Please select exactly 2 teams to swap');
      setSnackbarOpen(true);
      return;
    }

    if (teamOne.id === teamTwo.id) {
      return;
    }

    // Validate that both teams have exactly one current round game
    const team1Games = teamOne.currentRoundOpponents;
    const team2Games = teamTwo.currentRoundOpponents;

    if (!team1Games || team1Games.length === 0) {
      setSnackbarMessage(`${teamOne.name} has no current round games`);
      setSnackbarOpen(true);
      return;
    }

    if (!team2Games || team2Games.length === 0) {
      setSnackbarMessage(`${teamTwo.name} has no current round games`);
      setSnackbarOpen(true);
      return;
    }

    if (team1Games.length > 1) {
      setSnackbarMessage(`${teamOne.name} has multiple games in current round. Cannot swap.`);
      setSnackbarOpen(true);
      return;
    }

    if (team2Games.length > 1) {
      setSnackbarMessage(`${teamTwo.name} has multiple games in current round. Cannot swap.`);
      setSnackbarOpen(true);
      return;
    }

    // Validate that teams won't play against themselves after swap
    const team1CurrentOpponent = team1Games[0];
    const team2CurrentOpponent = team2Games[0];

    // Check if Team 1 would play against itself after swap
    if (team1CurrentOpponent.teamId === teamTwo.id) {
      setSnackbarMessage(
        `Cannot swap: ${teamOne.name} would end up playing against ${teamTwo.name}, but ${teamTwo.name} is the team being swapped in.`
      );
      setSnackbarOpen(true);
      return;
    }

    // Check if Team 2 would play against itself after swap
    if (team2CurrentOpponent.teamId === teamOne.id) {
      setSnackbarMessage(
        `Cannot swap: ${teamTwo.name} would end up playing against ${teamOne.name}, but ${teamOne.name} is the team being swapped in.`
      );
      setSnackbarOpen(true);
      return;
    }

    // Additional check: if both teams are currently playing against each other
    if (team1CurrentOpponent.teamId === teamTwo.id && team2CurrentOpponent.teamId === teamOne.id) {
      setSnackbarMessage(
        `Cannot swap: ${teamOne.name} and ${teamTwo.name} are currently playing against each other. Swapping would create the same matchup.`
      );
      setSnackbarOpen(true);
      return;
    }

    try {
      const teamA = {
        id: teamOne.id,
        gameId: team1Games[0].gameId
      };

      const teamB = {
        id: teamTwo.id,
        gameId: team2Games[0].gameId
      };

      const result = await dispatch(
        swapTeamsInGames({
          eventId,
          teamA,
          teamB
        })
      ).unwrap();

      setSnackbarMessage(result.message || `Successfully swapped ${teamOne.name} and ${teamTwo.name}`);
      setSnackbarOpen(true);

      setSelectedTeams([]);
      setFocusedTeamId('');
      dispatch(getEventPlayerOpponentsByRound({ eventId }));
    } catch (error) {
      console.error('Swap failed:', error);
      setSnackbarMessage(`Failed to swap teams: ${error.message || error}`);
      setSnackbarOpen(true);
    }
  };

  const handleDragStart = (e, teamId) => {
    if (swappingTeams || applyingAutoAdjustments) {
      return;
    }

    const team = buildSwapTeam(teamId);
    if (!team?.currentRoundOpponents?.length) {
      return;
    }

    e.dataTransfer.setData('text/plain', teamId);
    e.dataTransfer.effectAllowed = 'move';
    setDragSourceTeamId(teamId);
  };

  const handleDragOver = (e, targetTeamId) => {
    if (!dragSourceTeamId || dragSourceTeamId === targetTeamId) {
      return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTeamId(targetTeamId);
  };

  const handleDrop = async (e, targetTeamId) => {
    e.preventDefault();

    const sourceTeamId = e.dataTransfer.getData('text/plain') || dragSourceTeamId;
    setDragSourceTeamId('');
    setDragOverTeamId('');

    if (!sourceTeamId || !targetTeamId || sourceTeamId === targetTeamId) {
      return;
    }

    const sourceTeam = buildSwapTeam(sourceTeamId);
    const targetTeam = buildSwapTeam(targetTeamId);
    await executeSwap(sourceTeam, targetTeam);
  };

  const handleDragEnd = () => {
    setDragSourceTeamId('');
    setDragOverTeamId('');
  };

  // Function to handle the swap operation
  const handleSwapTeams = async () => {
    if (selectedTeams.length !== 2) {
      setSnackbarMessage('Please select exactly 2 teams to swap');
      setSnackbarOpen(true);
      return;
    }

    await executeSwap(selectedTeams[0], selectedTeams[1]);
  };

  // Function to count how many times two teams have played against each other
  const getOpponentCount = (teamData, opponentId, untilRoundInclusive = null) => {
    let count = 0;
    Object.entries(teamData.opponentsByRound || {}).forEach(([roundKey, opponents]) => {
      const roundNumber = Number(roundKey);
      if (untilRoundInclusive !== null && roundNumber > untilRoundInclusive) {
        return;
      }

      // Handle both old string format and new object format
      opponents.forEach((opponent) => {
        const id = typeof opponent === 'string' ? opponent : opponent.teamId;
        if (id === opponentId) {
          count++;
        }
      });
    });
    return count;
  };

  // Function to get color based on opponent count
  const getOpponentColor = (count) => {
    if (count === 1) return 'success'; // Green
    if (count === 2) return 'warning'; // Orange/Yellow
    if (count >= 3) return 'error'; // Red
    return 'default'; // Default color
  };

  const refreshAutoAdjustments = (showMessage = false) => {
    const suggestions = buildTeamOpponentAutoAdjustments(teamOpponents, maxRounds);
    setAutoAdjustments(suggestions);

    if (showMessage) {
      if (suggestions.length === 0) {
        setSnackbarMessage('No auto-corrections found for current round conflicts.');
        setSnackbarOpen(true);
        return;
      }

      setSnackbarMessage(`${suggestions.length} auto-corrections prepared. Review and apply.`);
      setSnackbarOpen(true);
    }
  };

  const generateAutoAdjustments = () => refreshAutoAdjustments(true);

  useEffect(() => {
    if (!teamOpponents || Object.keys(teamOpponents).length === 0 || !maxRounds) {
      setAutoAdjustments([]);
      return;
    }

    refreshAutoAdjustments(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamOpponents, maxRounds]);

  const applyAutoAdjustments = async () => {
    if (autoAdjustments.length === 0) {
      setSnackbarMessage('No prepared auto-corrections to apply.');
      setSnackbarOpen(true);
      return;
    }

    setApplyingAutoAdjustments(true);
    let appliedCount = 0;
    let failedCount = 0;
    let latestOpponents = teamOpponents;
    const maxIterations = Math.max(Object.keys(teamOpponents || {}).length, 1) * 2;
    let iteration = 0;

    try {
      while (iteration < maxIterations) {
        const currentSuggestions = buildTeamOpponentAutoAdjustments(latestOpponents, maxRounds);
        if (!currentSuggestions.length) {
          break;
        }

        // Always apply the next suggestion from the latest state.
        const adjustment = currentSuggestions[0];
        const teamAData = latestOpponents?.[adjustment.fromTeamId];
        const teamBData = latestOpponents?.[adjustment.toTeamId];
        const teamACurrent = teamAData?.opponentsByRound?.[maxRounds] || [];
        const teamBCurrent = teamBData?.opponentsByRound?.[maxRounds] || [];

        const gameA = teamACurrent.length === 1 ? teamACurrent[0] : null;
        const gameB = teamBCurrent.length === 1 ? teamBCurrent[0] : null;

        if (!gameA?.gameId || !gameB?.gameId) {
          failedCount += 1;
          break;
        }

        try {
          await dispatch(
            swapTeamsInGames({
              eventId,
              teamA: { id: adjustment.fromTeamId, gameId: gameA.gameId },
              teamB: { id: adjustment.toTeamId, gameId: gameB.gameId }
            })
          ).unwrap();

          latestOpponents = await dispatch(getEventPlayerOpponentsByRound({ eventId })).unwrap();
          appliedCount += 1;
        } catch (swapError) {
          failedCount += 1;
          break;
        }

        iteration += 1;
      }

      await dispatch(getEventPlayerOpponentsByRound({ eventId }));
      setSelectedTeams([]);
      setFocusedTeamId('');
      setAutoAdjustments([]);
      setSnackbarMessage(
        `Auto-correction complete. Applied: ${appliedCount}${failedCount ? `, Failed: ${failedCount}` : ''}`
      );
      setSnackbarOpen(true);
    } finally {
      setApplyingAutoAdjustments(false);
    }
  };

  const applySingleAutoAdjustment = async (adjustment) => {
    if (!adjustment?.fromTeamId || !adjustment?.toTeamId) {
      return;
    }

    setApplyingAutoAdjustments(true);

    try {
      const teamAData = teamOpponents?.[adjustment.fromTeamId];
      const teamBData = teamOpponents?.[adjustment.toTeamId];
      const teamACurrent = teamAData?.opponentsByRound?.[maxRounds] || [];
      const teamBCurrent = teamBData?.opponentsByRound?.[maxRounds] || [];

      const gameA = teamACurrent.length === 1 ? teamACurrent[0] : null;
      const gameB = teamBCurrent.length === 1 ? teamBCurrent[0] : null;

      if (!gameA?.gameId || !gameB?.gameId) {
        setSnackbarMessage('Unable to apply this suggestion due to missing current round games.');
        setSnackbarOpen(true);
        return;
      }

      await dispatch(
        swapTeamsInGames({
          eventId,
          teamA: { id: adjustment.fromTeamId, gameId: gameA.gameId },
          teamB: { id: adjustment.toTeamId, gameId: gameB.gameId }
        })
      ).unwrap();

      await dispatch(getEventPlayerOpponentsByRound({ eventId }));
      setSelectedTeams([]);
      setFocusedTeamId('');
      setSnackbarMessage(`Applied suggestion: ${adjustment.fromTeamName} -> ${adjustment.toTeamName}`);
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Failed to apply suggestion: ${error?.message || error}`);
      setSnackbarOpen(true);
    } finally {
      setApplyingAutoAdjustments(false);
    }
  };

  const renderOpponents = (opponents, teamData, isCurrentRound = false, isSelected = false) => {
    if (!opponents || opponents.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No opponents
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative'
        }}>
        {opponents.map((opponent, index) => {
          // Handle both old string format and new object format
          const opponentName = typeof opponent === 'string' ? opponent : opponent.teamName;
          const opponentId = typeof opponent === 'string' ? opponent : opponent.teamId;
          const gameId = typeof opponent === 'object' ? opponent.gameId : null;
          const opponentCount = getOpponentCount(teamData, opponentId);
          const chipColor = getOpponentColor(opponentCount);
          const isFocusedAppearance = Boolean(focusedTeamId) && focusedTeamId === opponentId;

          return (
            <Chip
              key={index}
              label={`${opponentName} (${opponentCount})`}
              size="small"
              color={chipColor}
              variant={isFocusedAppearance ? 'outlined' : 'filled'}
              title={gameId ? `Game ID: ${gameId}` : undefined}
              sx={{
                fontSize: '0.75rem',
                height: '24px',
                borderWidth: isFocusedAppearance ? 2 : 0,
                borderColor: isFocusedAppearance ? selectionBlue : undefined,
                backgroundColor: isFocusedAppearance ? selectionBlueLight : undefined,
                color: isFocusedAppearance ? selectionBlue : undefined,
                '& .MuiChip-label': {
                  padding: '0 8px'
                }
              }}
            />
          );
        })}
        {/* Selection indicator in current round */}
        {isCurrentRound && isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 20,
              height: 20,
              backgroundColor: selectionBlue,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.7rem',
              color: '#fff',
              fontWeight: 'bold',
              zIndex: 1
            }}>
            {selectedTeams.findIndex((team) => team.id === teamData.id) + 1}
          </Box>
        )}
      </Box>
    );
  };

  if (loadingPlayerOpponents) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
        gap={2}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading team opponents...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'string' ? error : 'Failed to load team opponents data'}
        </Alert>
      </Box>
    );
  }

  if (!teamOpponents || Object.keys(teamOpponents).length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">No team opponent data available for this event.</Alert>
      </Box>
    );
  }

  const teams = Object.entries(teamOpponents);

  // Create historical rounds array (all rounds except the max/current one, in reverse order)
  const historicalRounds = Array.from({ length: maxRounds - 1 }, (_, i) => maxRounds - 1 - i);

  return (
    <>
      {/* Loading backdrop for team swapping */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={swappingTeams || applyingAutoAdjustments}>
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">
          {applyingAutoAdjustments ? 'Applying auto-corrections...' : 'Swapping teams...'}
        </Typography>
        <Typography variant="body2">Please wait while we update the games.</Typography>
      </Backdrop>

      <Box p={3}>
        <Box mb={2}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/events/${eventId}`)}>
            Back to Event
          </Button>
        </Box>

        <Typography variant="h4" gutterBottom>
          Team Opponents by Round
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          This table shows which opponents each team has faced in each round, helping to avoid
          repeat matchups in future rounds. Click on the current round cells to select teams for
          swapping. Click a team name to highlight where that team appears as an opponent.
        </Typography>

        {/* Swap Controls */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Selected Teams:
          </Typography>
          {selectedTeams.map((team, index) => (
            <Chip key={team.id} label={team.name} color="primary" variant="outlined" size="small" />
          ))}
          {selectedTeams.length === 0 && (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              None selected
            </Typography>
          )}

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<AutoFixHighIcon />}
              onClick={generateAutoAdjustments}
              disabled={swappingTeams || applyingAutoAdjustments || maxRounds <= 1}
              size="small">
              Regenerate Preview
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={
                applyingAutoAdjustments ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoFixHighIcon />
                )
              }
              onClick={applyAutoAdjustments}
              disabled={
                autoAdjustments.length === 0 || swappingTeams || applyingAutoAdjustments
              }
              size="small">
              {applyingAutoAdjustments ? 'Applying...' : `Apply Auto-Correct (${autoAdjustments.length})`}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<ClearIcon />}
              onClick={() => {
                handleClearSelection();
                setFocusedTeamId('');
              }}
              disabled={selectedTeams.length === 0 && !Boolean(focusedTeamId) || swappingTeams}
              size="small">
              Clear
            </Button>
            <Button
              variant="contained"
              startIcon={
                swappingTeams ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />
              }
              onClick={handleSwapTeams}
              disabled={selectedTeams.length !== 2 || swappingTeams}
              size="small">
              {swappingTeams ? 'Swapping...' : 'Swap Teams'}
            </Button>
          </Box>
        </Box>

        {autoAdjustments.length > 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Proposed adjustments for current round (Round {maxRounds}) (review before applying):
            </Typography>
            {autoAdjustments.map((item, index) => (
              <Box
                key={`${item.fromTeamId}-${item.toTeamId}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                <Typography variant="body2">
                  {index + 1}. Row pair {item.rowPair}: swap {item.fromTeamName} with {item.toTeamName}{' '}
                  ({item.fixedTeamName || 'Fixed team'} should play {item.toTeamName}; {item.fromTeamName}{' '}
                  should play {item.affectedTeamName})
                  {item.unavoidableRepeat ? ' - unavoidable repeat remains' : ''}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={swappingTeams || applyingAutoAdjustments}
                  onClick={() => applySingleAutoAdjustment(item)}>
                  Apply
                </Button>
              </Box>
            ))}
          </Alert>
        ) : null}

        {/* Legend */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Legend:
          </Typography>
          <Chip
            label="1 time"
            size="small"
            color="success"
            variant="filled"
            sx={{ fontSize: '0.75rem' }}
          />
          <Chip
            label="2 times"
            size="small"
            color="warning"
            variant="filled"
            sx={{ fontSize: '0.75rem' }}
          />
          <Chip
            label="3+ times"
            size="small"
            color="error"
            variant="filled"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>

        {/* Table with loading overlay */}
        <Box position="relative">
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: '80vh',
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              opacity: swappingTeams ? 0.5 : 1,
              pointerEvents: swappingTeams ? 'none' : 'auto',
              transition: 'opacity 0.3s ease'
            }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {/* Team Name Column */}
                  <StyledTableCell
                    sx={{
                      minWidth: '200px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3
                    }}>
                    Team Name
                  </StyledTableCell>

                  {/* Current Round Column */}
                  <StyledTableCell
                    align="center"
                    sx={{
                      minWidth: '250px',
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText
                    }}>
                    Round {maxRounds} (Current) - Click to Select or Drag to Swap
                  </StyledTableCell>

                  {/* Gap Column */}
                  <GapTableCell>{/* Empty gap column */}</GapTableCell>

                  {/* Historical Rounds Columns */}
                  {historicalRounds.map((round) => (
                    <StyledTableCell key={round} align="center" sx={{ minWidth: '220px' }}>
                      Round {round}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map(([teamId, teamData]) => {
                  const isSelected = selectedTeams.find((team) => team.id === teamId);
                  const currentRoundOpponents = teamData.opponentsByRound?.[maxRounds];
                  const hasCurrentRoundOpponents =
                    currentRoundOpponents && currentRoundOpponents.length > 0;
                  const isDragSource = dragSourceTeamId === teamId;
                  const isDragOver = dragOverTeamId === teamId && dragSourceTeamId !== teamId;

                  return (
                    <SelectableTeamRow key={teamId} selected={!!isSelected}>
                      {/* Team Name with Rank */}
                      <OpponentCell
                        sx={{
                          fontWeight: 'bold',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: theme.palette.background.paper,
                          zIndex: 2,
                          cursor: hasCurrentRoundOpponents && !swappingTeams ? 'pointer' : 'default'
                        }}>
                        <Box
                          onClick={() => handleFocusSelection(teamId)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            borderRadius: 1,
                            px: 0.5,
                            py: 0.25,
                            backgroundColor: focusedTeamId === teamId ? selectionBlueLight : 'transparent',
                            border: focusedTeamId === teamId ? `1px solid ${selectionBlue}` : '1px solid transparent',
                            cursor: 'pointer'
                          }}>
                          <Chip
                            label={teamData.rank || '?'}
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={{
                              fontSize: '0.75rem',
                              height: '20px',
                              minWidth: '24px',
                              '& .MuiChip-label': {
                                padding: '0 4px'
                              }
                            }}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {teamData.name}
                          </Typography>
                          {isSelected && (
                            <Chip
                              label={selectedTeams.findIndex((team) => team.id === teamId) + 1}
                              size="small"
                              color="primary"
                              variant="filled"
                              sx={{
                                fontSize: '0.7rem',
                                height: '18px',
                                minWidth: '18px'
                              }}
                            />
                          )}
                        </Box>
                      </OpponentCell>

                      {/* Current Round - Clickable */}
                      <CurrentRoundCell
                        selected={!!isSelected}
                        hasOpponents={hasCurrentRoundOpponents && !swappingTeams}
                        draggable={
                          hasCurrentRoundOpponents && !swappingTeams && !applyingAutoAdjustments
                        }
                        onDragStart={(e) => handleDragStart(e, teamId)}
                        onDragOver={(e) => handleDragOver(e, teamId)}
                        onDrop={(e) => handleDrop(e, teamId)}
                        onDragEnd={handleDragEnd}
                        onDragLeave={() => {
                          if (dragOverTeamId === teamId) {
                            setDragOverTeamId('');
                          }
                        }}
                        onClick={(e) =>
                          hasCurrentRoundOpponents &&
                          handleSwapSelection(e, teamId, teamData.name, currentRoundOpponents)
                        }
                        sx={{
                          opacity: isDragSource ? 0.55 : 1,
                          outline: isDragOver ? `2px dashed ${selectionBlue}` : 'none',
                          outlineOffset: isDragOver ? '-2px' : 0
                        }}>
                        {renderOpponents(
                          currentRoundOpponents,
                          { ...teamData, id: teamId },
                          true,
                          !!isSelected
                        )}
                      </CurrentRoundCell>

                      {/* Gap Cell */}
                      <GapCell>{/* Empty gap cell */}</GapCell>

                      {/* Historical Rounds */}
                      {historicalRounds.map((round) => (
                        <OpponentCell key={round}>
                          {renderOpponents(teamData.opponentsByRound?.[round], teamData)}
                        </OpponentCell>
                      ))}
                    </SelectableTeamRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Total Teams: {teams.length} | Max Rounds Played: {maxRounds}
          </Typography>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </>
  );
};

export default TeamOpponents;
